import Spinner from 'britive-design-system/core/components/spinner'
import ConstraintDrawerTable from './ConstraintDrawerTable'
import React, { useState } from 'react'
import { useFetchPermissionData, useTableData } from './hooks'
import PermissionDetails from './ConstraintPermissionDetails'
import ModalPopup from 'britive-design-system/core/components/modal-popup'
import './index.scss'
import { useDispatch, useSelector } from 'react-redux'
import { addPermissionConstraints } from 'action_creators/permissions_constraints'
import toast from 'utils/toast'

const ConstraintDrawerContainer = ({
  isDrawerOpen,
  profileId,
  permissionName,
  permissionType,
  permissionConstraintTypes,
  permissionDescription,
  viewOnly = false,
  toggleDrawer,
}) => {
  const dispatch = useDispatch()

  const [isSaveDisabled, setIsSaveDisabled] = useState(true)

  const { loading } = useFetchPermissionData({
    profileId,
    permissionName,
    permissionType,
    permissionConstraintTypes,
  })

  const { permissionConstraints, metadataConstraints } = useSelector(state => ({
    permissionConstraints:
      state.permissionsConstraints.permissionConstraints[
        permissionConstraintTypes[0]
      ],
    metadataConstraints:
      state.permissionsConstraints?.permissionMetadataConstraints?.result,
  }))

  const [tableData, setTableData] = useTableData(
    metadataConstraints,
    permissionConstraints
  )

  const validateRequiredFields = data =>
    data.some(row => {
      if (!row.mandatory) return false
      if (row.type === 'text' || row.type === 'secret')
        return !row.value || row.value.trim() === ''
      if (row.type === 'number')
        return row.value === '' || row.value == null || isNaN(row.value)

      return false
    })

  const handleTableDataChange = constraintData => {
    const changedFields = getChangedFields(constraintData, permissionConstraints)
    setTableData(constraintData)
    if (!validateRequiredFields(constraintData)) {
      setIsSaveDisabled(Boolean(!changedFields.length))
    }
  }

  const refinePayload = (data, operation) => {
    if (operation === 'remove') {
      return data.filter(row => row.clearValue).map(rows => rows.name)
    }
    return data
      .filter(row => {
        if (operation === 'add' && row.mandatory) return true
        if (row.type === 'number' && !row.systemDefined)
          return row.value !== '' && row.value !== null && !isNaN(row.value)
        return row.value?.toString()
      })
      .map(({ name, value, systemDefined }) => ({
        name,
        value,
        systemDefined: systemDefined,
      }))
  }

  const getOperationType = (currentData, existingData) => {
    if (!existingData?.result) {
      return ['add']
    }
    let op = []
    if (existingData?.result && currentData.some(item => !item.clearValue)) {
      op.push('update')
    }
    const isVariableMarkedForDeletion = currentData.some(item => item.clearValue)
    if (isVariableMarkedForDeletion) {
      op.push('remove')
    }
    return op
  }

  const getChangedFields = (tableData, permissionConstraints) => {
    const existingResults = permissionConstraints?.result || []

    return tableData.filter(currentRow => {
      const prevRow = existingResults.find(r => r.name === currentRow.name)

      const currentValue = currentRow.value
      const prevValue = prevRow ? prevRow.value : undefined

      const isCurrentValueEmpty =
        currentValue === '' || currentValue === null || currentValue === undefined

      const isPrevValueEmpty =
        prevValue === '' || prevValue === null || prevValue === undefined

      if (!prevRow && !isCurrentValueEmpty) {
        return true
      }

      if (prevRow) {
        if (isCurrentValueEmpty && !isPrevValueEmpty) {
          return true
        }
        if (
          !isCurrentValueEmpty &&
          !isPrevValueEmpty &&
          String(currentValue) !== String(prevValue)
        ) {
          return true
        }
      }

      return false
    })
  }

  const executeOperations = async (changedFields, operations) => {
    try {
      if (changedFields.length > 0) {
        for (const operation of operations) {
          const payload = refinePayload(changedFields, operation)
          await dispatch(
            addPermissionConstraints(
              profileId,
              permissionName,
              permissionType,
              permissionConstraintTypes,
              payload,
              operation
            )
          )
        }
        toast({
          title: 'Settings saved successfully!',
          type: 'success',
          time: 'normal',
        })
      }
    } catch (error) {
      toast({ title: error.toString(), type: 'error', time: 'normal' })
    }
  }

  const onSavePermissionConstraint = () => {
    const changedFields = getChangedFields(tableData, permissionConstraints)
    const operations = getOperationType(changedFields, permissionConstraints)
    executeOperations(changedFields, operations)
    toggleDrawer()
  }

  const renderButtons = () =>
    !viewOnly && [
      {
        text: 'Save',
        variant: 'primary',
        disabled: isSaveDisabled,
        onClick: onSavePermissionConstraint,
        size: 'medium',
      },
      {
        text: 'Close',
        variant: 'secondary',
        onClick: toggleDrawer,
        size: 'medium',
      },
    ]

  if (!isDrawerOpen) return null

  return (
    <ModalPopup
      width={720}
      title={viewOnly ? 'Permission Variables' : 'Manage Permissions'}
      onCancel={toggleDrawer}
      buttons={renderButtons()}
    >
      {loading ? (
        <div className="constraint-drawer-loading-container">
          <Spinner size="medium" message="Loading..." />
        </div>
      ) : (
        <>
          <PermissionDetails
            permissionName={permissionName}
            permissionDescription={permissionDescription}
            permissionType={permissionType}
          />
          <br />
          <br />
          <ConstraintDrawerTable
            viewOnly={viewOnly}
            tableData={tableData}
            systemDefinedValues={metadataConstraints?.systemDefinedValues}
            onTableDataChange={handleTableDataChange}
          />
        </>
      )}
    </ModalPopup>
  )
}

export default ConstraintDrawerContainer
